import { type SelectProps } from 'antd';
import { useFormikContext } from 'formik';
import { uniqBy } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type ModuleSpecsFragment } from '../../api/fragments/ModuleSpecs';
import { useListLocationsQuery, useListMobilityHomeDeliveriesQuery } from '../../api/queries';
import { type LocationFilteringRule } from '../../api/types';
import { useCompany } from '../contexts/CompanyContextManager';
import SelectField, { type SelectFieldProps } from './SelectField';

type LocationSelectSharedProps = {
    skipCondition?: boolean;
    addNoneOption?: boolean;
    parentName?: string;
    module?: ModuleSpecsFragment;
};

export type LocationSelectFieldProps = Omit<SelectFieldProps, 'children' | 'options'> &
    LocationFilteringRule &
    LocationSelectSharedProps;

export type UselocationSelectFieldOptionsArgs = LocationSelectSharedProps & {
    filter: LocationFilteringRule;
    module?: ModuleSpecsFragment;
};

export enum LocationSelection {
    HomeDelivery = 'homeDelivery',
    PickUp = 'pickUp',
    None = 'none',
}

export const useLocationSelectFieldOptions = ({
    filter,
    module,
    skipCondition,
    addNoneOption,
}: UselocationSelectFieldOptionsArgs) => {
    const { t } = useTranslation(['moduleDetails', 'common']);
    const company = useCompany(true);

    const { data, loading } = useListLocationsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { ...filter, companyId: filter?.companyId || company?.id } },
    });

    const { data: deliveryData, loading: deliveryLoading } = useListMobilityHomeDeliveriesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                companyId: filter.companyId || company?.id,
                customerModuleId: module?.__typename === 'LocalCustomerManagementModule' ? module?.id : null,
                agreementModuleId: module?.__typename === 'ConsentsAndDeclarationsModule' ? module?.id : null,
            },
        },
    });

    const resultsLocations = useMemo(() => data?.locations?.items, [data?.locations?.items]);

    const { locations, locationOptions } = useMemo(() => {
        const isMoreThanOneModule = uniqBy('moduleName', resultsLocations).length > 1;
        const options = (resultsLocations || []).map(result => ({
            value: result.id,
            label: t('common:pickUpLocation', {
                locationPickUpName: result.name,
                moduleDisplayName: isMoreThanOneModule ? `(${result?.moduleName})` : '',
            }),
            type: LocationSelection.PickUp,
            isHomeDelivery: result.isHomeDelivery || false,
        }));

        return {
            locations: resultsLocations,
            locationOptions: options,
        };
    }, [resultsLocations, t]);

    const resultsDeliveries = useMemo(() => deliveryData?.delivery?.items, [deliveryData?.delivery?.items]);

    const { deliveries, deliveriesOption } = useMemo(() => {
        const isMoreThanOneModule =
            uniqBy(
                'id',
                (resultsDeliveries || [])
                    .filter(result => result.homeDelivery.isEnable)
                    .flatMap(result => result.homeDelivery)
            ).length > 1;
        const options = (resultsDeliveries || []).map(
            result =>
                result.homeDelivery.isEnable && {
                    value: result.homeDelivery.id,
                    label: t('common:homeDeliveryOption', {
                        moduleDisplayName: isMoreThanOneModule ? `(${result?.displayName})` : '',
                    }),
                    type: LocationSelection.HomeDelivery,
                    isHomeDelivery: true,
                }
        );

        return {
            deliveries: resultsDeliveries,
            deliveriesOption: options,
        };
    }, [resultsDeliveries, t]);

    const options = useMemo(
        () =>
            [
                addNoneOption && {
                    value: null,
                    label: t('moduleDetails:selectFields.options.none'),
                    type: LocationSelection.None,
                },
                ...locationOptions,
                ...deliveriesOption,
            ].filter(Boolean),
        [addNoneOption, deliveriesOption, locationOptions, t]
    );

    return { locations, options, loading, deliveries, deliveryLoading };
};

const filterOption: SelectProps['filterOption'] = (inputValue, option) =>
    option.label.toString().toLowerCase().includes(inputValue.toLowerCase());

const LocationSelectField = ({
    companyId,
    addNoneOption,
    skipCondition,
    parentName,
    module,
    ...props
}: LocationSelectFieldProps) => {
    const { locations, options, loading, deliveries, deliveryLoading } = useLocationSelectFieldOptions({
        filter: { companyId },
        module,
        skipCondition,
        addNoneOption,
    });

    const { setFieldValue } = useFormikContext();

    const handleChange = useCallback(
        (newValue?: string) => {
            setFieldValue(
                `${parentName}.isHomeDelivery`,
                deliveries?.some(delivery => delivery.homeDelivery.id === newValue) ?? false
            );
        },
        [deliveries, parentName, setFieldValue]
    );

    return (
        <SelectField
            {...props}
            filterOption={filterOption}
            loading={!locations && loading && !deliveries && deliveryLoading}
            onChange={handleChange}
            options={options}
        />
    );
};

export default LocationSelectField;
